<template>
  <div
    class="financial-summary-modal"
    @dragenter.prevent
    @dragover.prevent
    @drop.prevent
  >
    <!-- Title Bar with Refresh and Close Buttons -->
    <div class="title-bar">
      <div class="title-content">
        <h3>Financial Summary</h3>
        <select
          v-model="selectedYear"
          @change="onYearChange"
          class="year-selector"
        >
          <option v-for="yr in availableYears" :key="yr" :value="yr">
            {{ yr }}
          </option>
        </select>
      </div>
      <div class="title-buttons">
        <button
          v-if="hasAccess"
          class="share-btn"
          @click="openShareDialog"
          title="Share Financial Summary"
        >
          <i class="fas fa-share-alt"></i>
        </button>
        <button
          class="excel-btn"
          @click="exportToExcel"
          title="Export to Excel"
        >
          <i class="fas fa-file-excel"></i>
        </button>
        <button
          class="print-btn"
          @click="printFinancialSummary"
          title="Print Financial Summary"
        >
          <i class="fas fa-print"></i>
        </button>
        <button
          class="refresh-btn"
          @click="refreshFinancialData"
          title="Refresh Data"
        >
          <i class="fas fa-sync-alt"></i>
        </button>
        <button class="close-btn" @click="closeFinancialSummary">
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>

    <!-- Financial Summary Table -->
    <!-- Financial Summary Table -->
    <div
      class="table-container"
      :class="{ 'drag-over': dragOver }"
      @dragenter.prevent="handleDragEnter"
      @dragover.prevent="handleDragOver"
      @dragleave.prevent="handleDragLeave"
      @drop.prevent="handleDrop"
    >
      <input
        type="file"
        ref="fileInput"
        accept=".pdf"
        @change="handleFileSelect"
        style="display: none"
      />

      <!-- Upload Message -->
      <div
        v-if="uploadMessage"
        class="upload-message"
        :class="uploadMessageType"
      >
        {{ uploadMessage }}
      </div>

      <!-- Drag Drop Overlay -->
      <div v-if="dragOver" class="drag-overlay">
        <div class="drag-content">
          <i class="fas fa-file-pdf drag-icon"></i>
          <p class="drag-text">Drop bank statement PDF here</p>
        </div>
      </div>
      <table id="printableFinancialTable" class="financial-table">
        <thead>
          <!-- Table Title Row -->
          <tr class="table-title-row">
            <th colspan="16" class="table-title">{{ reportTitle }}</th>
          </tr>
          <tr class="header-row">
            <th class="item-col">Item</th>
            <th class="month-col clickable-month" @click="handleMonthClick(0)">
              Jan
            </th>
            <th class="month-col clickable-month" @click="handleMonthClick(1)">
              Feb
            </th>
            <th class="month-col clickable-month" @click="handleMonthClick(2)">
              Mar
            </th>
            <th class="month-col clickable-month" @click="handleMonthClick(3)">
              Apr
            </th>
            <th class="month-col clickable-month" @click="handleMonthClick(4)">
              May
            </th>
            <th class="month-col clickable-month" @click="handleMonthClick(5)">
              Jun
            </th>
            <th class="month-col clickable-month" @click="handleMonthClick(6)">
              Jul
            </th>
            <th class="month-col clickable-month" @click="handleMonthClick(7)">
              Aug
            </th>
            <th class="month-col clickable-month" @click="handleMonthClick(8)">
              Sep
            </th>
            <th class="month-col clickable-month" @click="handleMonthClick(9)">
              Oct
            </th>
            <th class="month-col clickable-month" @click="handleMonthClick(10)">
              Nov
            </th>
            <th class="month-col clickable-month" @click="handleMonthClick(11)">
              Dec
            </th>
            <th class="total-col">Total</th>
            <th class="stats-col">Stats</th>
            <th class="note-col">Note</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, index) in tableData"
            :key="`row-${row.id || index}`"
            :class="getRowClasses(row, index)"
          >
            <!-- Item Column -->
            <td
              class="item-cell"
              :class="{
                indented: getIndentLevel(row.sequence) > 0,
                'sumof-clickable': row.rowType === 'subTotal' && row.sumOf,
                'query-clickable': row.rowType === 'query',
              }"
              @click="handleItemClick(row)"
              @dblclick="handleItemDoubleClick(row)"
            >
              <span
                :class="{
                  'bold-item':
                    row.rowType === 'subTotal' || row.rowType === 'grandTotal',
                  'indent-1': getIndentLevel(row.sequence) === 1,
                  'indent-2': getIndentLevel(row.sequence) === 2,
                }"
              >
                {{ row.item }}
              </span>
            </td>

            <!-- Monthly Columns -->
            <td
              v-for="month in months"
              :key="month"
              class="month-cell"
              :class="{
                editable: isEditable(row.rowType),
                'highlight-add': highlightedRows.includes(row.item + '_add'),
                'highlight-subtract': highlightedRows.includes(
                  row.item + '_subtract',
                ),
                'highlight-sum': highlightMode === row.id,
                'uncategorized-clickable':
                  row.item === 'Uncategorized Expenses' && row[month],
                'query-clickable': row.rowType === 'query' && row[month],
              }"
              @click="startEdit(row, month)"
              @dblclick="handleUncategorizedClick(row, month)"
            >
              <input
                v-if="
                  editingCell &&
                  editingCell.rowId === row.id &&
                  editingCell.month === month
                "
                v-model="editingValue"
                @blur="finishEdit"
                @keyup.enter="finishEdit"
                @keyup.tab="finishEdit"
                @keyup.escape="cancelEdit"
                class="edit-input"
                type="text"
                ref="editInput"
              />
              <span v-else class="cell-value">
                {{ formatNumber(row[month]) }}
              </span>
            </td>

            <!-- Total Column -->
            <td
              class="total-cell"
              :class="{
                'annual-clickable': row.rowType === 'query',
              }"
              @dblclick="handleTotalDoubleClick(row)"
            >
              {{ formatNumber(row.total) }}
            </td>

            <!-- Stats Column -->
            <td class="stats-cell">
              {{ formatPercent(row.stats) }}
            </td>

            <!-- Note Column -->
            <td
              class="note-cell"
              :title="row.note"
              :class="{
                'note-editable': true,
              }"
              @dblclick="startNoteEdit(row)"
            >
              <input
                v-if="editingNote && editingNote.rowId === row.id"
                v-model="editingNoteValue"
                @blur="finishNoteEdit"
                @keyup.enter="finishNoteEdit"
                @keyup.tab="finishNoteEdit"
                @keyup.escape="cancelNoteEdit"
                class="edit-note-input"
                type="text"
                ref="editNoteInput"
              />
              <span v-else class="note-value">
                {{ row.note }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Loading indicator -->
    <div v-if="loading" class="loading-indicator">
      <i class="fas fa-spinner fa-spin"></i> Loading financial data...
    </div>

    <!-- Error message -->
    <div v-if="error" class="error-message">
      <i class="fas fa-exclamation-triangle"></i> {{ error }}
    </div>

    <!-- Last refreshed timestamp -->
    <div class="last-refreshed">last refreshed: {{ lastRefreshed }}</div>

    <!-- Share Dialog -->
    <div
      v-if="showShareDialog"
      class="dialog-overlay"
      @click="closeShareDialog"
    >
      <div class="dialog-content" @click.stop>
        <div class="dialog-header">
          <h3>Share Financial Summary</h3>
          <button @click="closeShareDialog" class="close-dialog-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="dialog-body">
          <p>Copy this URL to share the Financial Summary view:</p>
          <input
            type="text"
            :value="shareUrl"
            readonly
            class="share-url-input"
            @click="$event.target.select()"
          />
        </div>
        <div class="dialog-footer">
          <button @click="copyShareUrl" class="copy-btn">
            <i class="fas fa-copy"></i> Copy URL
          </button>
          <button @click="closeShareDialog" class="cancel-btn">Close</button>
        </div>
      </div>
    </div>

    <!-- Query Accounts Popup -->
    <div
      v-if="showQueryAccountsPopup"
      class="accounts-overlay"
      @click.self="closeQueryAccountsPopup"
    >
      <div class="accounts-container">
        <div class="accounts-header">
          <div class="header-content">
            <h2>{{ queryAccountsData.itemName }}</h2>
            <div class="header-subtitle">
              Accounts in query ({{ queryAccountsData.accounts.length }})
            </div>
          </div>
          <button class="close-button" @click="closeQueryAccountsPopup">
            ×
          </button>
        </div>

        <div class="accounts-body">
          <table
            class="accounts-table"
            v-if="queryAccountsData.accounts.length > 0"
          >
            <thead>
              <tr>
                <th>Account #</th>
                <th>Account Name</th>
                <th>Type</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="account in queryAccountsData.accounts"
                :key="account.accountNumber"
              >
                <td class="account-number">{{ account.accountNumber }}</td>
                <td>{{ account.accountName }}</td>
                <td>{{ account.accountType }}</td>
              </tr>
            </tbody>
          </table>

          <div v-else class="empty-state">
            <i class="fas fa-info-circle"></i>
            <p>No matching accounts found</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axiosConfig';
import io from 'socket.io-client';

export default {
  name: 'FinancialSummary',
  props: {
    initialYear: {
      type: [Number, String],
      default: null,
    },
    userRole: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      selectedYear: null, // Will be set in created()
      tableData: [],
      loading: false,
      error: null,
      editingCell: null,
      editingValue: '',
      originalValue: '',
      editingNote: null,
      editingNoteValue: '',
      originalNoteValue: '',
      dragOver: false,
      uploading: false,
      uploadMessage: '',
      uploadMessageType: 'success',
      highlightedRows: [], // Track highlighted rows for sumOf visualization (changed to array for reactivity)
      highlightMode: null, // Track which row is currently showing highlights
      socket: null, // Socket.io connection
      lastRefreshed: '', // Track when data was last refreshed
      months: [
        'jan',
        'feb',
        'mar',
        'apr',
        'may',
        'jun',
        'jul',
        'aug',
        'sep',
        'oct',
        'nov',
        'dec',
      ],
      showShareDialog: false,
      shareUrl: '',
      showQueryAccountsPopup: false,
      queryAccountsData: {
        itemName: '',
        accounts: [],
      },
      annualDetailCategories: [], // no longer used - all query rows support annual detail
    };
  },
  computed: {
    year() {
      return this.selectedYear;
    },

    availableYears() {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let y = currentYear; y >= 2020; y--) {
        years.push(y);
      }
      return years;
    },

    reportTitle() {
      return `${this.year} Bank Transaction Business Summary`;
    },

    hasAccess() {
      return this.userRole < 2;
    },

    // Debug computed property to check highlighting
    debugInfo() {
      return {
        highlightedRows: this.highlightedRows,
        highlightMode: this.highlightMode,
        highlightCount: this.highlightedRows.length,
      };
    },
  },
  created() {
    // Set default year: use initialYear prop if provided,
    // otherwise default to previous year if current month < 3 (Jan/Feb)
    if (this.initialYear) {
      this.selectedYear = parseInt(this.initialYear, 10);
    } else {
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth(); // 0-indexed (0 = Jan, 1 = Feb, etc.)
      this.selectedYear = currentMonth < 2 ? currentYear - 1 : currentYear;
    }
  },

  mounted() {
    // Check user permissions
    if (this.userRole >= 2) {
      console.warn(
        'FinancialSummary: Access denied - insufficient permissions',
      );
      this.$emit('close');
      return;
    }

    this.loadFinancialData();
    this.connectSocket();
  },

  beforeUnmount() {
    // Clean up socket connection when component is destroyed
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  },
  watch: {
    selectedYear() {
      this.loadFinancialData();
    },
  },
  methods: {
    onYearChange() {
      // Explicitly load data when year changes from dropdown
      // This ensures data fetch even if watcher has timing issues
      this.loadFinancialData();
    },

    async loadFinancialData() {
      this.loading = true;
      this.error = null;
      try {
        // Add cache-busting timestamp to ensure fresh data
        const timestamp = Date.now();
        const response = await axios.get(
          `/api/financial-summary/${this.year}?t=${timestamp}`,
        );

        // Ensure the response data is properly assigned
        if (response.data && Array.isArray(response.data)) {
          this.tableData = response.data;
          // Update last refreshed timestamp
          this.updateLastRefreshed();
        } else {
          throw new Error('Invalid response format');
        }
      } catch (error) {
        console.error('Error loading financial data:', error);
        this.error = 'Failed to load financial data. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    updateLastRefreshed() {
      const now = new Date();
      const month = (now.getMonth() + 1).toString();
      const day = now.getDate().toString();
      const year = now.getFullYear().toString().slice(-2);
      let hours = now.getHours();
      const minutes = now.getMinutes().toString().padStart(2, '0');
      const ampm = hours >= 12 ? 'pm' : 'am';
      hours = hours % 12;
      hours = hours ? hours : 12; // the hour '0' should be '12'

      this.lastRefreshed = `${month}/${day}/${year} at ${hours}:${minutes}${ampm}`;
    },

    async exportToExcel() {
      try {
        // Check if there is data to export
        if (!this.tableData || this.tableData.length === 0) {
          alert('No data to export. Please wait for data to load.');
          return;
        }

        const response = await axios.post(
          '/api/export-financial-summary',
          { data: this.tableData, year: this.year },
          { responseType: 'blob' },
        );

        // Extract filename from Content-Disposition header
        const contentDisposition = response.headers['content-disposition'];
        let filename = `financial_summary_${this.year}.xlsx`;
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="(.+)"/);
          if (filenameMatch && filenameMatch.length === 2)
            filename = filenameMatch[1];
        }

        // Create a URL for the file and trigger a download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error exporting to Excel:', error);
        alert('Failed to export to Excel. Please try again.');
      }
    },

    printFinancialSummary() {
      const table = document.getElementById('printableFinancialTable');
      const printContent = table.outerHTML;

      const printWindow = window.open('', '_blank');
      printWindow.document.open();
      printWindow.document.write(`
        <html>
          <head>
            <title>Financial Summary - ${this.reportTitle}</title>
            <style>
              /* Set landscape orientation with minimal margins */
              @page {
                size: landscape;
                margin: 0.5in 0.25in;
              }

              /* General table styles for printing */
              body {
                font-family: Arial, sans-serif;
                margin: 0;
                padding: 10px;
              }
              
              table {
                width: 100%;
                border-collapse: collapse;
                font-size: 0.65em;
              }
              
              table, th, td {
                border: 1px solid #ddd;
              }
              
              th, td {
                padding: 3px 4px;
                text-align: left;
                vertical-align: top;
              }
              
              /* Zebra-striped rows */
              tbody tr:nth-child(even) {
                background-color: #e8e8e8 !important;
              }
              
              tbody tr:nth-child(odd) {
                background-color: #ffffff !important;
              }
              
              /* Right-align number columns (months, total, stats) but keep Item and Note left-aligned */
              td:nth-child(n+2):nth-child(-n+15) {
                text-align: right;
                font-family: 'Courier New', monospace;
              }
              
              /* Keep Item column (1st) and Note column (16th) left-aligned */
              td:first-child,
              td:last-child {
                text-align: left;
                font-family: inherit;
              }
              
              /* Make Item column narrower to prevent wrapping */
              td:first-child,
              th:first-child {
                width: 160px;
                min-width: 160px;
                white-space: nowrap;
              }
              
              /* Make other columns more compact */
              td:nth-child(n+2):nth-child(-n+15),
              th:nth-child(n+2):nth-child(-n+15) {
                width: 60px;
                min-width: 60px;
              }
              
              /* Note column gets remaining space and is wider */
              td:last-child,
              th:last-child {
                width: auto;
                min-width: 160px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                max-width: 200px;
              }
              
              /* Indenting for nested items */
              .indent-1 {
                margin-left: 20px;
              }
              
              .indent-2 {
                margin-left: 40px;
              }
              
              /* Header styling */
              .table-title {
                font-size: 1.3em;
                font-weight: bold;
                text-align: center;
                background-color: #f5f5f5 !important;
                padding: 8px;
              }
              
              .header-row th {
                background-color: #e9e9e9 !important;
                font-weight: bold;
                text-align: center;
              }
              
              /* Right-align header columns for numbers but keep Item and Note centered/left */
              .header-row th:nth-child(n+2):nth-child(-n+15) {
                text-align: right;
              }
              
              .header-row th:first-child,
              .header-row th:last-child {
                text-align: left;
              }
              
              /* Row styling */
              .subtotal-row {
                background-color: #f0f0f0 !important;
                font-weight: bold;
              }
              
              .grandtotal-row {
                background-color: #d9d9d9 !important;
                font-weight: bold;
              }
              
              .query-row {
                background-color: #f9f9f9 !important;
              }
              
              .manual-row {
                background-color: #fff !important;
              }
              
              /* Border styles */
              .border-top {
                border-top: 2px solid #999 !important;
              }
              
              .border-bottom {
                border-bottom: 2px solid #999 !important;
              }
              
              .border-both {
                border-top: 2px solid #999 !important;
                border-bottom: 2px solid #999 !important;
              }
              
              /* Force browsers to print background colors */
              @media print {
                body {
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
                }
              }
            </style>
          </head>
          <body onload="window.print(); window.close();">
            ${printContent}
          </body>
        </html>
      `);
      printWindow.document.close();
    },

    handleItemClick(row) {
      console.log(
        'Item clicked:',
        row.item,
        'rowType:',
        row.rowType,
        'sumOf:',
        row.sumOf,
      );

      // Temporary visual feedback
      this.uploadMessage = `Clicked: ${row.item} (${row.rowType})`;
      this.uploadMessageType = 'info';
      setTimeout(() => {
        this.uploadMessage = '';
      }, 2000);

      // Only handle clicks on subTotal rows with sumOf expressions
      if (row.rowType !== 'subTotal' || !row.sumOf) {
        console.log('Not a subTotal with sumOf, ignoring click');
        return;
      }

      // Toggle highlighting: if this row is already highlighted, turn off highlighting
      if (this.highlightMode === row.id) {
        console.log('Clearing highlighting for row:', row.id);
        this.clearHighlighting();
      } else {
        console.log('Highlighting sumOf items for row:', row.id);
        this.highlightSumOfItems(row);
      }
    },

    highlightSumOfItems(row) {
      console.log('Starting to highlight items for:', row.item);
      this.clearHighlighting();
      this.highlightMode = row.id;

      // Parse the sumOf expression
      const items = row.sumOf
        .split(',')
        .map((name) => name.trim())
        .map((name) => ({
          name: name.startsWith('-') ? name.substring(1) : name,
          operation: name.startsWith('-') ? 'subtract' : 'add',
        }));

      console.log('Parsed items:', items);

      // Add items to highlighted array
      items.forEach((item) => {
        const highlightKey = item.name + '_' + item.operation;
        this.highlightedRows.push(highlightKey);
        console.log('Added highlight key:', highlightKey);
      });

      console.log('Current highlighted rows:', this.highlightedRows);
    },

    clearHighlighting() {
      console.log('Clearing highlighting');
      this.highlightedRows = [];
      this.highlightMode = null;
    },

    async handleItemDoubleClick(row) {
      // Only handle double-clicks on query-type rows
      if (row.rowType !== 'query') {
        return;
      }

      try {
        const response = await axios.get(
          `/api/financial-summary/query-accounts/${encodeURIComponent(row.item)}`,
        );

        if (response.data) {
          this.queryAccountsData = {
            itemName: response.data.itemName || row.item,
            accounts: response.data.accounts || [],
          };
          this.showQueryAccountsPopup = true;
        }
      } catch (error) {
        console.error('Error fetching query accounts:', error);
        this.showUploadMessage(
          error.response?.data?.error || 'Failed to load account information',
          'error',
        );
      }
    },

    closeQueryAccountsPopup() {
      this.showQueryAccountsPopup = false;
      this.queryAccountsData = {
        itemName: '',
        accounts: [],
      };
    },

    async handleTotalDoubleClick(row) {
      if (row.rowType !== 'query') {
        return;
      }

      try {
        const response = await axios.get(
          `/api/financial-summary/${this.year}/category-annual/${encodeURIComponent(row.item)}`,
        );

        if (response.data && response.data.length > 0) {
          this.$emit('show-annual-detail', {
            categoryName: row.item,
            transactions: response.data,
            year: this.year,
          });
        } else {
          this.showUploadMessage(
            'No transaction details available for this category.',
            'info',
          );
        }
      } catch (error) {
        console.error('Error fetching annual category detail:', error);
        this.showUploadMessage(
          'Failed to load annual category details',
          'error',
        );
      }
    },

    // Debug method to check current state
    debugHighlighting() {
      console.log('Current highlighted rows:', this.highlightedRows);
      console.log('Current highlight mode:', this.highlightMode);
    },

    getRowClasses(row, index) {
      const classes = [];

      // Zebra striping
      if (index % 2 === 1) {
        classes.push('zebra-stripe');
      }

      // Border classes
      if (row.borders === 'top' || row.borders === 'both') {
        classes.push('border-top');
      }
      if (row.borders === 'bottom' || row.borders === 'both') {
        classes.push('border-bottom');
      }

      // Row type classes
      classes.push(`row-${row.rowType}`);

      return classes;
    },

    getIndentLevel(sequence) {
      if (!sequence) return 0;
      const matches = sequence.toString().match(/^-+/);
      return matches ? matches[0].length : 0;
    },

    isEditable(rowType) {
      // Only manual rows are editable - bankStatements and query rows are no longer editable
      return rowType === 'manual';
    },

    startEdit(row, month) {
      if (!this.isEditable(row.rowType)) return;

      this.editingCell = { rowId: row.id, month };
      this.editingValue = this.parseNumberForEdit(row[month]);
      this.originalValue = this.editingValue;

      // Focus the input on next tick
      this.$nextTick(() => {
        const input = this.$refs.editInput;
        if (input && input[0]) {
          input[0].focus();
          input[0].select();
        }
      });
    },

    async finishEdit() {
      if (!this.editingCell) return;

      const numericValue = this.parseEditedValue(this.editingValue);
      const row = this.tableData.find((r) => r.id === this.editingCell.rowId);

      if (row && numericValue !== this.parseEditedValue(this.originalValue)) {
        try {
          // Update the local data immediately
          row[this.editingCell.month] = numericValue;

          // Send update to server
          await this.updateCell(
            this.editingCell.rowId,
            this.editingCell.month,
            numericValue,
          );

          // Recalculate totals
          this.recalculateRowTotal(row);
        } catch (error) {
          console.error('Error updating cell:', error);
          // Revert the change
          row[this.editingCell.month] = this.parseEditedValue(
            this.originalValue,
          );
          this.error = 'Failed to save changes. Please try again.';
        }
      }

      this.cancelEdit();
    },

    cancelEdit() {
      this.editingCell = null;
      this.editingValue = '';
      this.originalValue = '';
    },

    startNoteEdit(row) {
      // Allow note editing for all row types
      this.editingNote = { rowId: row.id };
      this.editingNoteValue = row.note || '';
      this.originalNoteValue = this.editingNoteValue;

      // Focus the input on next tick
      this.$nextTick(() => {
        const input = this.$refs.editNoteInput;
        if (input && input[0]) {
          input[0].focus();
          input[0].select();
        }
      });
    },

    async finishNoteEdit() {
      if (!this.editingNote) return;

      const row = this.tableData.find((r) => r.id === this.editingNote.rowId);

      if (row && this.editingNoteValue !== this.originalNoteValue) {
        try {
          // Update the local data immediately
          row.note = this.editingNoteValue;

          // Send update to server
          await this.updateNote(this.editingNote.rowId, this.editingNoteValue);
        } catch (error) {
          console.error('Error updating note:', error);
          // Revert the change
          row.note = this.originalNoteValue;
          this.error = 'Failed to save note changes. Please try again.';
        }
      }

      this.cancelNoteEdit();
    },

    cancelNoteEdit() {
      this.editingNote = null;
      this.editingNoteValue = '';
      this.originalNoteValue = '';
    },

    async updateNote(rowId, note) {
      try {
        await axios.put(`/api/financial-summary/${rowId}/note`, { note });
      } catch (error) {
        console.error('Error updating note:', error);
        throw error;
      }
    },

    async updateCell(rowId, month, value) {
      try {
        await axios.put(`/api/financial-summary/${rowId}/${month}`, { value });
      } catch (error) {
        console.error('Error updating cell:', error);
        throw error;
      }
    },

    recalculateRowTotal(row) {
      let total = 0;
      this.months.forEach((month) => {
        const value = parseFloat(row[month]) || 0;
        total += value;
      });
      row.total = total;
    },

    parseNumberForEdit(value) {
      if (!value || value === 0) return '';
      const num = parseFloat(value);
      return isNaN(num) ? '' : num.toString();
    },

    parseEditedValue(value) {
      if (!value || value.trim() === '') return 0;
      // Remove commas and handle parentheses for negative numbers
      const cleaned = value.toString().replace(/,/g, '').replace(/[()]/g, '');
      const num = parseFloat(cleaned);
      // If the original had parentheses, make it negative
      return value.includes('(') ? -Math.abs(num) : num;
    },

    formatNumber(value) {
      if (!value || value === 0) return '';

      const num = parseFloat(value);
      if (isNaN(num) || num === 0) return '';

      const absNum = Math.abs(num);
      const formatted = absNum.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });

      return num < 0 ? `(${formatted})` : formatted;
    },

    formatPercent(value) {
      if (!value || value === 0) return '';

      const num = parseFloat(value);
      if (isNaN(num) || num === 0) return '';

      const formatted = num.toFixed(1);
      return formatted.endsWith('.0')
        ? formatted.slice(0, -2) + '%'
        : formatted + '%';
    },

    // Drag and Drop methods
    handleDragEnter(event) {
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = 'copy';
      this.dragOver = true;
    },

    handleDragOver(event) {
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = 'copy';
      this.dragOver = true;
    },

    handleDragLeave(event) {
      event.preventDefault();
      event.stopPropagation();
      // Only hide drag over if we're leaving the container completely
      if (!event.currentTarget.contains(event.relatedTarget)) {
        this.dragOver = false;
      }
    },

    handleDrop(event) {
      event.preventDefault();
      event.stopPropagation();
      this.dragOver = false;

      const files = Array.from(event.dataTransfer.files);
      const pdfFile = files.find((file) => file.type === 'application/pdf');

      if (pdfFile) {
        this.uploadPDF(pdfFile);
      } else {
        this.showUploadMessage('Please drop a PDF file', 'error');
      }
    },

    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file && file.type === 'application/pdf') {
        this.uploadPDF(file);
      } else {
        this.showUploadMessage('Please select a PDF file', 'error');
      }
      // Clear the input so the same file can be selected again
      event.target.value = '';
    },

    async uploadPDF(file) {
      this.uploading = true;
      this.uploadMessage = '';

      try {
        const formData = new FormData();
        formData.append('bankStatement', file);

        const response = await axios.post(
          '/api/financial-summary/upload-pdf',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          },
        );

        if (response.data.success) {
          this.showUploadMessage(
            'Bank statement processed successfully!',
            'success',
          );
          // Reload the financial data to show updates
          await this.loadFinancialData();
        } else {
          this.showUploadMessage('Failed to process bank statement', 'error');
        }
      } catch (error) {
        console.error('Error uploading PDF:', error);
        this.showUploadMessage(
          error.response?.data?.error || 'Failed to upload PDF',
          'error',
        );
      } finally {
        this.uploading = false;
      }
    },

    showUploadMessage(message, type) {
      this.uploadMessage = message;
      this.uploadMessageType = type;

      // Clear message after 5 seconds
      setTimeout(() => {
        this.uploadMessage = '';
      }, 5000);
    },

    async refreshFinancialData() {
      // Clear any highlighting and editing states before refresh
      this.clearHighlighting();
      this.cancelEdit();
      this.cancelNoteEdit();

      // Show brief feedback that refresh was initiated
      this.uploadMessage = 'Refreshing financial data...';
      this.uploadMessageType = 'info';

      // Clear the current data to ensure a fresh load
      this.tableData = [];

      try {
        await this.loadFinancialData();

        // Show success message briefly
        this.uploadMessage = 'Data refreshed successfully';
        this.uploadMessageType = 'success';
        setTimeout(() => {
          this.uploadMessage = '';
        }, 2000);
      } catch (error) {
        console.error('Error refreshing financial data:', error);
        this.uploadMessage = 'Failed to refresh data';
        this.uploadMessageType = 'error';
        setTimeout(() => {
          this.uploadMessage = '';
        }, 3000);
      }
    },

    closeFinancialSummary() {
      this.$emit('close');
    },

    openShareDialog() {
      // Check user permissions for sharing
      if (this.userRole >= 2) {
        console.warn('Share functionality denied - insufficient permissions');
        return;
      }

      // Construct the share URL with financials parameter
      const currentUrl = new URL(window.location.href);
      currentUrl.search = ''; // Clear existing query parameters
      const baseUrl = currentUrl.toString();

      this.shareUrl = `${baseUrl}?page=financials`;
      this.showShareDialog = true;
    },

    closeShareDialog() {
      this.showShareDialog = false;
    },

    async copyShareUrl() {
      try {
        await navigator.clipboard.writeText(this.shareUrl);
        // Provide feedback that the URL was copied
        const copyBtn = document.querySelector('.copy-btn');
        copyBtn.innerHTML = '<i class="fas fa-check"></i> Copied!';
        setTimeout(() => {
          copyBtn.innerHTML = '<i class="fas fa-copy"></i> Copy URL';
        }, 1500);
      } catch (err) {
        console.error('Failed to copy to clipboard:', err);
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = this.shareUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);

        // Still show feedback
        const copyBtn = document.querySelector('.copy-btn');
        copyBtn.innerHTML = '<i class="fas fa-check"></i> Copied!';
        setTimeout(() => {
          copyBtn.innerHTML = '<i class="fas fa-copy"></i> Copy URL';
        }, 1500);
      }
    },

    handleMonthClick(monthIndex) {
      // Open drilldown in a new tab with month filter
      const monthNames = [
        'jan',
        'feb',
        'mar',
        'apr',
        'may',
        'jun',
        'jul',
        'aug',
        'sep',
        'oct',
        'nov',
        'dec',
      ];
      const monthName = monthNames[monthIndex];
      const baseUrl = window.location.origin + window.location.pathname;
      const drilldownUrl = `${baseUrl}?page=banktransactions&month=${monthName}&year=${this.year}`;
      window.open(drilldownUrl, '_blank');
    },

    async handleUncategorizedClick(row, month) {
      console.log('handleUncategorizedClick called:', {
        item: row.item,
        month,
        rowType: row.rowType,
        value: row[month],
      });

      // Handle clicks on query rows OR Uncategorized Expenses (which is subTotal)
      const isUncategorized = row.item === 'Uncategorized Expenses';
      const isQueryRow = row.rowType === 'query';

      if (!isUncategorized && !isQueryRow) {
        console.log('Skipping - not a query row or uncategorized');
        return;
      }

      // Parse the value - handle both numeric and formatted string values
      let value = row[month];
      if (typeof value === 'string') {
        // Remove parentheses and commas for negative values like "(555)"
        value = value.replace(/[(),]/g, '');
        // If it was in parentheses, it was negative
        if (row[month].includes('(')) {
          value = -parseFloat(value);
        } else {
          value = parseFloat(value);
        }
      } else {
        value = parseFloat(value);
      }

      console.log('Parsed value:', value);

      if (!value || isNaN(value) || value === 0) {
        console.log('Skipping - invalid value');
        return;
      }

      try {
        const monthIndex = this.months.indexOf(month);
        const categoryName = row.item;

        console.log(
          'Processing category:',
          categoryName,
          'monthIndex:',
          monthIndex,
        );

        // For Uncategorized Expenses, use the existing endpoint (doesn't need _ids column)
        if (categoryName === 'Uncategorized Expenses') {
          const endpoint = `/api/financial-summary/${this.year}/${monthIndex}/uncategorized`;
          const response = await axios.get(endpoint);

          if (response.data && response.data.length > 0) {
            this.$emit('show-uncategorized', {
              categoryName: categoryName,
              transactions: response.data,
              month: monthIndex,
              monthName: month,
              year: this.year,
            });
          } else {
            this.showUploadMessage(
              'No transaction details available. Please click the Refresh button to load transaction IDs.',
              'info',
            );
          }
          return;
        }

        // For other categories, check for IDs and query by IDs directly
        const monthAbbrev = month.toLowerCase().substring(0, 3);
        const idsColumn = `${monthAbbrev}_ids`;

        console.log(
          'Looking for IDs column:',
          idsColumn,
          'value:',
          row[idsColumn],
        );
        console.log('Available row keys:', Object.keys(row));

        if (!row[idsColumn]) {
          this.showUploadMessage(
            'No transaction IDs available. Please click the Refresh button to load transaction details.',
            'info',
          );
          return;
        }

        // Parse the IDs from the row
        const ids = row[idsColumn].split(',').map((id) => id.trim());

        // Fetch transaction details by IDs
        const response = await axios.post('/api/bank-transactions/by-ids', {
          ids,
        });

        if (response.data && response.data.length > 0) {
          this.$emit('show-uncategorized', {
            categoryName: categoryName,
            transactions: response.data,
            month: monthIndex,
            monthName: month,
            year: this.year,
          });
        } else {
          this.showUploadMessage(
            'No transaction details found for these IDs.',
            'info',
          );
        }
      } catch (error) {
        console.error('Error fetching transaction details:', error);
        this.showUploadMessage('Failed to load transaction details', 'error');
      }
    },

    // Socket connection methods
    connectSocket() {
      try {
        const socketUrl = process.env.VUE_APP_SOCKET_URL;

        if (!socketUrl) {
          console.error('Socket URL is undefined.');
          return;
        }

        const token = this.getAuthToken();

        this.socket = io(socketUrl, {
          query: {
            token: token,
            source: 'financialSummary',
          },
        });

        this.socket.on('connect', () => {
          console.log('FinancialSummary: Connected to WebSocket server');
        });

        // Listen for bank transaction updates
        this.socket.on('bank-transaction-updated', (data) => {
          console.log('FinancialSummary: Bank transaction updated:', data);
          this.handleBankTransactionUpdate(data);
        });

        // Also listen for the legacy transactions-changed event for compatibility
        this.socket.on('transactions-changed', () => {
          console.log(
            'FinancialSummary: Legacy transactions-changed event received',
          );
          this.handleBankTransactionUpdate({ operation: 'legacy' });
        });

        this.socket.on('disconnect', () => {
          console.log('FinancialSummary: Disconnected from WebSocket server');
        });
      } catch (error) {
        console.error(
          'FinancialSummary: Error connecting to WebSocket server:',
          error,
        );
      }
    },

    getAuthToken() {
      return localStorage.getItem('token');
    },

    async handleBankTransactionUpdate(data) {
      console.log('FinancialSummary: Handling bank transaction update:', data);

      // Show a brief message that data is being refreshed
      this.showUploadMessage('Bank data updated, refreshing...', 'info');

      try {
        // Refresh the financial data to reflect bank transaction changes
        await this.loadFinancialData();

        // Show success message
        this.showUploadMessage('Financial data refreshed', 'success');
      } catch (error) {
        console.error(
          'FinancialSummary: Error refreshing after bank transaction update:',
          error,
        );
        this.showUploadMessage('Failed to refresh financial data', 'error');
      }
    },
  },
};
</script>

<style scoped>
.financial-summary-modal {
  position: relative;
  width: 90vw;
  max-width: 1400px;
  height: 95vh;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
}

.title-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #a080a8;
  color: #fff;
  padding: 8px 15px;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  font-size: 1.4em;
  margin-bottom: 0;
  flex-shrink: 0;
}

.title-content h3 {
  margin: 0;
  color: #fff;
  font-size: 1em;
  font-weight: 600;
}

.title-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.year-selector {
  padding: 4px 8px;
  font-size: 0.9em;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: #fff;
  color: #333;
  cursor: pointer;
}

.year-selector:hover {
  border-color: #907793;
}

.year-selector:focus {
  outline: none;
  border-color: #907793;
  box-shadow: 0 0 3px rgba(144, 119, 147, 0.5);
}

.title-buttons {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-shrink: 0;
}

.refresh-btn {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px solid #e1dede;
  color: #eee;
  background: #907793;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  margin-right: 30px;
  margin-top: 2px;
}

.refresh-btn:hover {
  background: #a080a8;
  color: #fff;
  border-color: #fff;
}

.refresh-btn i {
  font-size: 12px;
}

.share-btn {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px solid #e1dede;
  color: #eee;
  background: #907793;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  margin-right: 30px;
  margin-top: 2px;
}

.share-btn:hover {
  background: #a080a8;
  color: #fff;
  border-color: #fff;
}

.share-btn i {
  font-size: 12px;
}

.excel-btn {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px solid #e1dede;
  color: #eee;
  background: #907793;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  margin-right: 30px;
  margin-top: 2px;
}

.excel-btn:hover {
  background: #217346;
  color: #fff;
  border-color: #fff;
}

.excel-btn i {
  font-size: 12px;
}

.print-btn {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px solid #e1dede;
  color: #eee;
  background: #907793;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  margin-right: 30px;
  margin-top: 2px;
}

.print-btn:hover {
  background: #a080a8;
  color: #fff;
  border-color: #fff;
}

.print-btn i {
  font-size: 12px;
}

.close-btn {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px solid #e1dede;
  color: #eee;
  background: #907793;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  font-size: 0.6em;
  transition: all 0.2s;
  flex-shrink: 0;
}

.close-btn:hover {
  background: #7a6b7d;
  color: #fff;
}

.table-container {
  flex: 1;
  overflow: auto;
  padding: 0;
  margin: 0 10px;
  position: relative;
  /* Hide scrollbars */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  transition: all 0.3s ease;
}

.table-container.drag-over {
  border: 3px dashed #7b2cbf !important;
  background-color: rgba(123, 44, 191, 0.1) !important;
  box-shadow: 0 0 20px rgba(123, 44, 191, 0.3);
}

.table-container.drag-over::before {
  content: 'Drop PDF bank statement here';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(123, 44, 191, 0.9);
  color: white;
  padding: 20px 40px;
  border-radius: 8px;
  font-size: 18px;
  font-weight: bold;
  z-index: 1000;
  pointer-events: none;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}

.table-container.uploading {
  opacity: 0.7;
}

.table-container::-webkit-scrollbar {
  display: none; /* Chrome, Safari and Opera */
}

/* Drag overlay */
.drag-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(142, 68, 173, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  border-radius: 8px;
}

.drag-content {
  text-align: center;
  color: white;
}

.drag-icon {
  font-size: 48px;
  margin-bottom: 16px;
  color: white;
}

.drag-text {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

/* Upload message */
.upload-message {
  position: absolute;
  top: 10px;
  right: 10px;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  z-index: 100;
  max-width: 300px;
}

.upload-message.success {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.upload-message.error {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.upload-message.info {
  background-color: #d1ecf1;
  color: #0c5460;
  border: 1px solid #bee5eb;
}

.financial-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.75em;
  font-family: Calibri, sans-serif;
  background: white;
}

.table-title-row {
  background: transparent;
}

.table-title {
  padding: 8px;
  text-align: center;
  font-weight: 600;
  color: #333;
  font-size: 1.1em;
  border: none;
  font-family: Calibri, sans-serif;
}

.header-row {
  background: #f8f9fa;
  border-top: 2px solid #ccc;
  border-bottom: 2px solid #ccc;
  position: sticky;
  top: 0;
  z-index: 10;
}

.header-row th {
  padding: 3px 4px;
  text-align: center;
  font-weight: 600;
  color: #333;
  border-right: 1px solid #f0f0f0;
  font-family: Calibri, sans-serif;
}

.header-row th.month-col {
  text-align: right;
}

.clickable-month {
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    color 0.2s ease;
  user-select: none;
}

.clickable-month:hover {
  background-color: #a080a8 !important;
  color: white !important;
}

.item-col {
  width: 30%;
  min-width: 150px;
  text-align: left !important;
}

.month-col {
  width: 5%;
  min-width: 60px;
  text-align: right;
}

.total-col {
  width: 8%;
  border-left: 2px solid #ccc !important;
}

.stats-col {
  width: 6%;
}

.note-col {
  width: 30%;
  text-align: left !important;
}

tbody tr {
  border-bottom: 1px solid #f5f5f5;
}

.zebra-stripe {
  background-color: #ededed;
}

.border-top td {
  border-top: 2px solid #ccc;
}

.border-bottom td {
  border-bottom: 2px solid #ccc;
}

td {
  padding: 1px 6px;
  text-align: right;
  border-right: 1px solid #f5f5f5;
  vertical-align: middle;
  font-family: Calibri, sans-serif;
}

.item-cell {
  text-align: left !important;
  font-weight: 500;
  font-family: Calibri, sans-serif;
  min-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.indented .indent-1 {
  margin-left: 20px;
}

.indented .indent-2 {
  margin-left: 40px;
}

.bold-item {
  font-weight: bold;
}

.total-cell {
  border-left: 2px solid #ccc;
  font-weight: 600;
}

.total-cell.annual-clickable {
  cursor: pointer;
}

.total-cell.annual-clickable:hover {
  background-color: #f0eaf2;
  text-decoration: underline;
}

.note-cell {
  text-align: left !important;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.note-cell.note-editable {
  cursor: pointer;
  transition: background-color 0.2s;
}

.note-cell.note-editable:hover {
  background-color: #f3e5f5;
}

.edit-note-input {
  width: 100%;
  border: 2px solid #9c27b0;
  background: white;
  padding: 4px 8px;
  font-family: inherit;
  font-size: 10px;
  border-radius: 4px;
  outline: none;
}

.note-value {
  display: block;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.month-cell.editable {
  cursor: pointer;
  transition: background-color 0.2s;
}

.month-cell.editable:hover {
  background-color: #e3f2fd;
}

.month-cell.uncategorized-clickable {
  cursor: pointer;
  transition: background-color 0.2s;
}

.month-cell.uncategorized-clickable:hover {
  background-color: #fff3e0;
  box-shadow: 0 0 4px rgba(255, 152, 0, 0.3);
}

.edit-input {
  width: 100%;
  border: 2px solid #2196f3;
  padding: 2px 4px;
  text-align: right;
  background: white;
  font-family: Calibri, sans-serif;
  font-size: 0.65em;
}

.edit-input:focus {
  outline: none;
  border-color: #1976d2;
}

.cell-value {
  display: block;
  width: 100%;
}

.loading-indicator {
  text-align: center;
  padding: 20px;
  color: #666;
}

.error-message {
  background: #ffebee;
  color: #c62828;
  padding: 10px 20px;
  border: 1px solid #ef9a9a;
  border-radius: 4px;
  margin: 10px 20px;
}

.row-grandTotal td {
  font-weight: bold;
  background-color: #f5f5f5;
}

.row-subTotal td {
  font-weight: 600;
  background-color: #fafafa;
}

/* Responsive adjustments */
@media (max-width: 1200px) {
  .financial-summary-modal {
    width: 95vw;
  }

  .financial-table {
    font-size: 0.65em;
  }

  .month-col {
    min-width: 45px;
    text-align: right;
  }
}

/* sumOf highlighting styles */
.sumof-clickable {
  cursor: pointer;
}

.sumof-clickable:hover {
  background-color: #f0f0f0;
}

.highlight-add {
  outline: 2px solid #2196f3 !important;
  outline-offset: -1px;
  background-color: rgba(33, 150, 243, 0.1) !important;
}

.highlight-subtract {
  outline: 2px solid #ff5722 !important;
  outline-offset: -1px;
  background-color: rgba(255, 34, 34, 0.1) !important;
}

.highlight-sum {
  outline: 2px solid #4caf50 !important;
  outline-offset: -1px;
  background-color: rgba(76, 175, 80, 0.1) !important;
}

.last-refreshed {
  position: absolute;
  bottom: 10px;
  right: 10px;
  font-size: 11px;
  color: #999;
  font-style: italic;
}

/* Share Dialog Styles */
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.dialog-content {
  background: white;
  border-radius: 8px;
  width: 500px;
  max-width: 90vw;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
}

.dialog-header h3 {
  margin: 0;
  color: #333;
}

.close-dialog-btn {
  background: none;
  border: none;
  font-size: 18px;
  color: #666;
  cursor: pointer;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-dialog-btn:hover {
  color: #333;
}

.dialog-body {
  padding: 20px;
}

.dialog-body p {
  margin: 0 0 15px 0;
  color: #555;
}

.share-url-input {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background: #f9f9f9;
  font-family: monospace;
}

.share-url-input:focus {
  outline: none;
  border-color: #907793;
  background: white;
}

.dialog-footer {
  display: flex;
  gap: 10px;
  padding: 20px;
  border-top: 1px solid #e0e0e0;
  justify-content: flex-end;
}

.copy-btn {
  background: #907793;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background 0.2s ease;
}

.copy-btn:hover {
  background: #a080a8;
}

.cancel-btn {
  background: #f5f5f5;
  color: #333;
  border: 1px solid #ddd;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s ease;
}

.cancel-btn:hover {
  background: #e8e8e8;
}

/* Query Accounts Popup Styles */
.accounts-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.25);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 20001;
}

.accounts-container {
  background-color: white;
  border-radius: 8px;
  width: 90%;
  max-width: 700px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.accounts-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px;
  background: #a080a8;
  color: white;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}

.accounts-header .header-content {
  flex: 1;
}

.accounts-header h2 {
  margin: 0 0 8px 0;
  font-size: 1.3em;
  font-weight: 600;
}

.accounts-header .header-subtitle {
  font-size: 0.95em;
  opacity: 0.95;
  font-weight: 400;
}

.accounts-header .close-button {
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 5px 10px;
  transition: opacity 0.2s;
  line-height: 1;
}

.accounts-header .close-button:hover {
  opacity: 0.8;
}

.accounts-body {
  flex: 1;
  overflow-y: auto;
  padding: 15px;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.accounts-body::-webkit-scrollbar {
  display: none;
}

.accounts-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9em;
}

.accounts-table th,
.accounts-table td {
  padding: 2px 12px;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
}

.accounts-table th {
  background: #f5f5f5;
  font-weight: 600;
  color: #333;
  position: sticky;
  top: 0;
}

.accounts-table tr:hover {
  background: #f9f9f9;
}

.accounts-table .account-number {
  font-family: 'Courier New', monospace;
  font-weight: 500;
  color: #a080a8;
}

.accounts-body .empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #999;
}

.accounts-body .empty-state i {
  font-size: 48px;
  color: #a080a8;
  margin-bottom: 16px;
}

.accounts-body .empty-state p {
  font-size: 1.1em;
  margin: 0;
}

/* Query-clickable item style */
.item-cell.query-clickable {
  cursor: pointer;
}

.item-cell.query-clickable:hover {
  text-decoration: underline;
  color: #a080a8;
}

/* Query-clickable month cell style */
.month-cell.query-clickable {
  cursor: pointer;
}

.month-cell.query-clickable:hover {
  background-color: #e8d8e8;
}
</style>
